import { useMemo } from 'react';

import { useRoleOptions } from '@/hooks/use-role-options.ts';
import useConfig from '@/hooks/useConfig.ts';
import useFilters from '@/hooks/useFilters.ts';

import { SelectionOption } from '@/models/SelectionOption.ts';

import { useFilterStore } from '@/stores/filterStore.ts';

import { PRACTICE_NONE, ROLE_NONE } from '@/utils/task-utils.ts';

interface ResolvedSelection {
    /** Dropdown options, including the leading "none" bucket sentinel. */
    all_options: SelectionOption[];
    /**
     * Effective selection for display and filtering: an unset value (`undefined`) defaults to all
     * options, while an explicit empty array is preserved as the "none" bucket (attribute-less
     * tasks only). Resolving at read time keeps the dropdown and the board in sync on first render.
     */
    values: string[];
}

export const useResolvedRoleSelection = (): ResolvedSelection => {
    const { location_key } = useConfig();
    const { roleOptions } = useRoleOptions();
    const stored = useFilterStore((state) => state.selected_roles[location_key]);

    const all_options = useMemo<SelectionOption[]>(
        () => [{ value: ROLE_NONE, label: 'Ohne Rolle' }, ...roleOptions],
        [roleOptions]
    );
    const values = useMemo(() => stored ?? all_options.map((option) => option.value), [stored, all_options]);

    return { all_options, values };
};

export const useResolvedPracticeSelection = (): ResolvedSelection => {
    const { location_key } = useConfig();
    const { practice_options } = useFilters();
    const stored = useFilterStore((state) => state.selected_practices[location_key]);

    const all_options = useMemo<SelectionOption[]>(
        () => [{ value: PRACTICE_NONE, label: 'Ohne Praxis' }, ...practice_options],
        [practice_options]
    );
    const values = useMemo(() => stored ?? all_options.map((option) => option.value), [stored, all_options]);

    return { all_options, values };
};
